/**
 * dataChannel — le protocole du canal de données de la salle, tel que le legacy l'écrit et le lit
 * (`common/hooks/livekit/useLiveKitEventSender.js`, `useLiveKitEventHandler.js`,
 * `useLiveKitEventListener.js`) : le même format, octet pour octet, pour que le tuteur, qui garde
 * l'ancien code, lise ce que l'élève envoie et l'inverse.
 *
 *  - un événement est `{ ...data, type }`, en JSON, passé par `encodeURIComponent`, puis un octet
 *    par caractère (la chaîne encodée n'est qu'ASCII) ;
 *  - au-delà de 32 Kio encodés, le JSON brut se coupe en tranches de 12 Kio, chacune dans une
 *    enveloppe `__chunk` (`chunk_id`, `chunk_index`, `chunk_total`, `chunk_payload`) encodée de même ;
 *  - à la lecture, UTF-8 puis `decodeURIComponent` ; une tranche attend ses sœurs 30 s au plus.
 *
 * Pur, sauf l'assembleur, qui tient ses tranches et leur minuteur.
 */

/** La limite d'un message du canal (65 535 octets), avec la marge de l'enveloppe. */
export const MAX_PAYLOAD_BYTES = 32 * 1024
/** Une tranche, sous la limite une fois enveloppée et encodée. */
export const CHUNK_BODY_BYTES = 12 * 1024
export const CHUNK_TYPE = "__chunk"
/** Une transmission en tranches dont une manque : abandonnée au bout de ce délai. */
export const CHUNK_TTL_MS = 30 * 1000

/** Un événement du canal : son `type` et ce qu'il porte. */
export type LiveDataEvent = { type: string } & Record<string, unknown>

type ChunkEnvelope = {
    type: typeof CHUNK_TYPE
    chunk_id: string
    chunk_index: number
    chunk_total: number
    chunk_payload: string
}

/** Un octet par caractère : la chaîne vient d'`encodeURIComponent`, donc de l'ASCII. */
export function stringToBytes(value: string): Uint8Array<ArrayBuffer> {
    const bytes = new Uint8Array(value.length)
    for (let index = 0; index < value.length; index++) bytes[index] = value.charCodeAt(index)
    return bytes
}

/** Les octets reçus → la chaîne JSON (UTF-8, puis `decodeURIComponent`), ou l'ASCII lisible en repli. */
export function bytesToString(bytes: Uint8Array): string {
    try {
        const decoder = new TextDecoder("utf-8", { fatal: false, ignoreBOM: true })
        return decodeURIComponent(decoder.decode(bytes))
    } catch {
        // Le repli d'avant : les caractères ASCII imprimables et les blancs, sans plus.
        return Array.from(bytes)
            .filter((byte) => byte >= 32 || byte === 9 || byte === 10 || byte === 13)
            .map((byte) => String.fromCharCode(byte))
            .join("")
    }
}

function randomChunkId(random: () => number, now: () => number): string {
    return now().toString(36) + random().toString(36).slice(2, 8)
}

/**
 * L'événement en messages prêts à partir : un seul en général, une suite de tranches s'il est
 * trop gros. Les tranches partent dans l'ordre, l'une après l'autre.
 */
export function encodeLiveEvent(
    type: string,
    data: Record<string, unknown> = {},
    deps: { random?: () => number; now?: () => number } = {}
): Uint8Array<ArrayBuffer>[] {
    const json = JSON.stringify({ ...data, type })
    const encoded = encodeURIComponent(json)
    if (encoded.length <= MAX_PAYLOAD_BYTES) return [stringToBytes(encoded)]

    const chunkId = randomChunkId(deps.random ?? Math.random, deps.now ?? Date.now)
    const total = Math.ceil(json.length / CHUNK_BODY_BYTES)
    const messages: Uint8Array<ArrayBuffer>[] = []
    for (let index = 0; index < total; index++) {
        const envelope: ChunkEnvelope = {
            type: CHUNK_TYPE,
            chunk_id: chunkId,
            chunk_index: index,
            chunk_total: total,
            chunk_payload: json.slice(index * CHUNK_BODY_BYTES, (index + 1) * CHUNK_BODY_BYTES),
        }
        messages.push(stringToBytes(encodeURIComponent(JSON.stringify(envelope))))
    }
    return messages
}

function isEnvelope(value: Record<string, unknown>): value is ChunkEnvelope {
    return (
        value.type === CHUNK_TYPE &&
        typeof value.chunk_id === "string" &&
        typeof value.chunk_index === "number" &&
        typeof value.chunk_total === "number" &&
        typeof value.chunk_payload === "string"
    )
}

export type ChunkAssembler = {
    /**
     * Un message reçu, déjà lu en JSON : l'événement complet qu'il termine, ou `null` (une tranche
     * qui attend ses sœurs, une enveloppe mal formée, ce qui n'est pas un objet).
     */
    push: (message: unknown) => LiveDataEvent | null
    /** Oublie les tranches en attente et coupe leurs minuteurs. */
    clear: () => void
}

/** Le remontage des tranches, comme `useLiveKitEventHandler` : par `chunk_id`, dans l'ordre. */
export function createChunkAssembler(): ChunkAssembler {
    const pending = new Map<
        string,
        { total: number; received: Map<number, string>; timer: ReturnType<typeof setTimeout> }
    >()

    const drop = (chunkId: string) => {
        const entry = pending.get(chunkId)
        if (entry != null) clearTimeout(entry.timer)
        pending.delete(chunkId)
    }

    return {
        push(message) {
            if (message == null || typeof message !== "object") return null
            const value = message as Record<string, unknown>
            if (value.type !== CHUNK_TYPE) {
                return typeof value.type === "string" ? (value as LiveDataEvent) : null
            }
            if (!isEnvelope(value)) return null

            let entry = pending.get(value.chunk_id)
            if (entry == null) {
                entry = {
                    total: value.chunk_total,
                    received: new Map(),
                    timer: setTimeout(() => drop(value.chunk_id), CHUNK_TTL_MS),
                }
                pending.set(value.chunk_id, entry)
            }
            entry.received.set(value.chunk_index, value.chunk_payload)
            if (entry.received.size < entry.total) return null

            let assembled = ""
            for (let index = 0; index < entry.total; index++) {
                const piece = entry.received.get(index)
                // Un trou : la transmission est abandonnée, le minuteur nettoiera.
                if (piece === undefined) return null
                assembled += piece
            }
            drop(value.chunk_id)
            const event = JSON.parse(assembled) as unknown
            return event != null && typeof event === "object" && typeof (event as LiveDataEvent).type === "string"
                ? (event as LiveDataEvent)
                : null
        },
        clear() {
            pending.forEach((entry) => clearTimeout(entry.timer))
            pending.clear()
        },
    }
}

/** L'id d'utilisateur d'une identité LiveKit (`"123.web"` → `"123"`). */
export function userIdOfIdentity(identity: string | null | undefined): string | null {
    if (identity == null || identity === "") return null
    return identity.split(".")[0] ?? null
}
